import { AbstractMesh, Mesh, TransformNode, Vector3 } from '@babylonjs/core';
import { ObstacleBehavior } from './behavior/ObstacleBehavior';
import { Entity } from './Entity';
import { Level } from './Level';
import { MaterialService } from './MaterialService';
import { ObstacleDistributionConfig } from './ObstacleGenerator';
import { PlayerCollision } from './PlayerCollision';

/**
 * Represents an obstacle instance in the game
 */
export class Obstacle implements Entity {
  constructor(
    public id: string,
    public mesh: Mesh,
    public behavior: ObstacleBehavior,
    public config: ObstacleDistributionConfig,
    public spawnPoint: Vector3,
    private level: Level,
    public pathIndex: number,
    public rootNode?: TransformNode
  ) {
    // Determine destructibility
    // Default: not destructible. Optional: configurable.
    // If destructible, player dash/projectile can destroy it.
    // If NOT destructible, player ALWAYS dies on contact.

    // Note: The PlayerCollision callback logic handles the interaction.
    // We attach it to the mesh metadata.

    this.setupCollision();

    // Initialize behavior
    this.behavior.attach(this.mesh);
    this.behavior.initLevel(this.level);
  }

  private setupCollision(): void {
    const isDestructible = this.config.destructible === true;
    const isHarmless = this.config.harmless === true;

    const collisionCallback = (level: Level, player: any, event: any) => {
      if (isDestructible && (player.dashing || player.isKicking || player.isAttack)) {
        // Player destroys obstacle
        level.particles.createDisappearanceParticleSystem(this.mesh, 0.4, MaterialService.colorRed);
        this.dispose();
      } else if (!isHarmless) {
        // Player dies
        player.collide(event, 'Killed by obstacle!');
      } else if (isHarmless) {
        // execute behavior collision
        this.behavior.playerCollision(player);
      }
    };

    // Apply to main mesh and all children
    const applyToMesh = (m: AbstractMesh) => {
      m.metadata = {
        ...m.metadata,
        playerCollision: new PlayerCollision(false, collisionCallback),
        projectileCollision: isDestructible,
        projectileDestoyable: isDestructible,
      };
      // Also register with player if player exists (handled by Generator usually, but good to ensure)
    };

    if (this.mesh instanceof AbstractMesh) {
      applyToMesh(this.mesh);
    }
    this.mesh
      .getChildMeshes(false, (n) => n instanceof AbstractMesh)
      .forEach((child) => {
        applyToMesh(child as AbstractMesh);
      });
  }

  /**
   * Update obstacle every frame
   */
  update(deltaTime: number): void {
    if (!this.mesh || this.mesh.isDisposed()) {
      return;
    }
    this.behavior.update(deltaTime);
  }

  /**
   * Dispose of the obstacle
   */
  dispose(): void {
    // Remove main mesh and all child meshes from collision meshes
    // AND unregister them from the player's ActionManager
    const meshesToRemove = [this.mesh, ...this.mesh.getChildMeshes(false, (n) => n instanceof AbstractMesh)];
    for (const meshToRemove of meshesToRemove) {
      if (this.level.player && meshToRemove instanceof Mesh) {
        this.level.player.unregisterCollisionMesh(meshToRemove);
      }

      if (meshToRemove instanceof Mesh) {
        const index = this.level.collisionMeshes.indexOf(meshToRemove);
        if (index > -1) {
          this.level.collisionMeshes.splice(index, 1);
        }
      }
    }

    // Detach behavior
    if (this.behavior) {
      this.behavior.detach();
    }

    // Dispose root node and all children, but NOT materials
    if (this.rootNode && !this.rootNode.isDisposed()) {
      this.rootNode.dispose(false, false);
    } else if (this.mesh && !this.mesh.isDisposed()) {
      this.mesh.dispose(false, false);
    }
  }
}
